import React, { useState, useEffect, useMemo } from 'react';
import PinGate from './PinGate';
import { db } from '../firebase';
import {
  collection, onSnapshot, doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc,
  serverTimestamp, query, orderBy
} from 'firebase/firestore';

// Phil-only billing page. Two locks: Firestore rules restrict these collections
// to the owner account, and a 4-digit PIN acts as a screen-privacy gate on top.

// One display row per CLIENT: services shown as pills on that line (order
// SEO → PPC → FB), totals summed across services. Phil is color-blind, so
// rows alternate between two blue tints of clearly different lightness.
const SERVICES = ['SEO', 'PPC', 'FB', 'Hosting', 'Other'];
const PILL = { bg: '#2563eb', text: '#ffffff' };
const ZEBRA = ['rgba(37, 99, 235, 0.16)', 'rgba(37, 99, 235, 0.04)'];
const serviceRank = (s) => { const i = SERVICES.indexOf(s); return i === -1 ? SERVICES.length : i; };
const COST_FIELDS = [
  { key: 'costBlogs',        label: 'Blogs' },
  { key: 'costCallTracking', label: 'Call Tracking' },
  { key: 'costPpc',          label: 'PPC Spend' },
  { key: 'costSocial',       label: 'Social Budget' },
  { key: 'costOther',        label: 'Other' },
];

const UNLOCK_KEY = 'residuals_unlocked';

async function sha256(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}

const money = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(Math.round(n)).toLocaleString();
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const hardCost = (r) => COST_FIELDS.reduce((s, f) => s + num(r[f.key]), 0);
const profit = (r) => num(r.retail) - hardCost(r);
const marginPct = (r) => (num(r.retail) > 0 ? (profit(r) / num(r.retail)) * 100 : null);
const marginColor = (m) => m === null ? 'var(--text-secondary)'
  : m < 15 ? 'var(--accent-red)' : m < 40 ? 'var(--accent-orange)' : 'var(--accent-green)';
const normName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

function nameMatch(a, b) {
  const x = normName(a), y = normName(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

// ---------- Row editor ----------
function RowEditor({ initial, onSave, onCancel }) {
  const [row, setRow] = useState({
    client: '', service: 'SEO', costBlogs: 0, costCallTracking: 0,
    costPpc: 0, costSocial: 0, costOther: 0, retail: 0, ...initial
  });
  const set = (k, v) => setRow(r => ({ ...r, [k]: v }));
  const numInput = (k) => (
    <input type="number" step="1" value={row[k]} onChange={e => set(k, e.target.value)}
      style={{ width: 90, padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: 8 }}>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Client<br />
        <input value={row.client} onChange={e => set('client', e.target.value)} placeholder="Client name"
          style={{ width: 190, padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
      </label>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Service<br />
        <select value={row.service} onChange={e => set('service', e.target.value)}
          style={{ padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
          {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      {COST_FIELDS.map(f => (
        <label key={f.key} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.label}<br />{numInput(f.key)}</label>
      ))}
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Retail<br />{numInput('retail')}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-sm btn-success" disabled={!row.client.trim()}
          onClick={() => onSave({
            client: row.client.trim(), service: row.service,
            costBlogs: num(row.costBlogs), costCallTracking: num(row.costCallTracking),
            costPpc: num(row.costPpc), costSocial: num(row.costSocial),
            costOther: num(row.costOther), retail: num(row.retail),
          })}>Save</button>
        <button className="btn btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ---------- Main ----------
export default function Residuals() {
  const [gate, setGate] = useState('loading'); // loading | denied | setup | locked | open
  const [gateError, setGateError] = useState('');
  const [pinHash, setPinHash] = useState(null);

  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({});
  const [expenses, setExpenses] = useState({ items: [] });
  const [clients, setClients] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addingFor, setAddingFor] = useState(null); // client name for quick "+ service"
  const [expanded, setExpanded] = useState(() => new Set());
  const [showChecks, setShowChecks] = useState(true);
  const [snapMsg, setSnapMsg] = useState('');

  // Gate: read PIN settings (this read also proves the account is allowed).
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'residualsMeta', 'settings'));
        const hash = snap.exists() ? snap.data().pinHash : null;
        setPinHash(hash);
        if (sessionStorage.getItem(UNLOCK_KEY) === '1' && hash) setGate('open');
        else setGate(hash ? 'locked' : 'setup');
      } catch (err) {
        setGate('denied');
      }
    })();
  }, []);

  // Data subscriptions once unlocked.
  useEffect(() => {
    if (gate !== 'open') return;
    const unsubs = [
      onSnapshot(collection(db, 'residuals'), s => setRows(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(doc(db, 'residualsMeta', 'settings'), s => setSettings(s.exists() ? s.data() : {})),
      onSnapshot(doc(db, 'residualsMeta', 'expenses'), s => setExpenses(s.exists() ? s.data() : { items: [] })),
      onSnapshot(collection(db, 'clients'), s => setClients(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(query(collection(db, 'residualsSnapshots'), orderBy('__name__', 'desc')), s =>
        setSnapshots(s.docs.map(d => ({ id: d.id, ...d.data() })))),
    ];
    return () => unsubs.forEach(u => u());
  }, [gate]);

  const handlePin = async (pin) => {
    setGateError('');
    const hash = await sha256(pin);
    if (gate === 'setup') {
      await setDoc(doc(db, 'residualsMeta', 'settings'), { pinHash: hash, updatedAt: serverTimestamp() }, { merge: true });
      setPinHash(hash);
      sessionStorage.setItem(UNLOCK_KEY, '1');
      setGate('open');
    } else if (hash === pinHash) {
      sessionStorage.setItem(UNLOCK_KEY, '1');
      setGate('open');
    } else {
      setGateError('Wrong PIN.');
    }
  };

  const lock = () => { sessionStorage.removeItem(UNLOCK_KEY); setGate('locked'); };

  // ----- computed -----
  const sorted = useMemo(() =>
    [...rows].sort((a, b) => a.client.localeCompare(b.client) || serviceRank(a.service) - serviceRank(b.service)),
    [rows]);

  // One entry per client: its service lines plus summed costs/retail/profit.
  const grouped = useMemo(() => {
    const map = new Map();
    sorted.forEach(r => {
      if (!map.has(r.client)) map.set(r.client, []);
      map.get(r.client).push(r);
    });
    return [...map.entries()].map(([client, list]) => {
      const g = { client, list };
      COST_FIELDS.forEach(f => { g[f.key] = list.reduce((s, r) => s + num(r[f.key]), 0); });
      g.retail = list.reduce((s, r) => s + num(r.retail), 0);
      g.cost = list.reduce((s, r) => s + hardCost(r), 0);
      g.profit = g.retail - g.cost;
      g.margin = g.retail > 0 ? (g.profit / g.retail) * 100 : null;
      return g;
    });
  }, [sorted]);

  const totals = useMemo(() => {
    const revenue = rows.reduce((s, r) => s + num(r.retail), 0);
    const cost = rows.reduce((s, r) => s + hardCost(r), 0);
    const gross = revenue - cost;
    const exp = (expenses.items || []).reduce((s, i) => s + num(i.amount), 0);
    const byField = {};
    COST_FIELDS.forEach(f => { byField[f.key] = rows.reduce((s, r) => s + num(r[f.key]), 0); });
    return { revenue, cost, gross, margin: revenue > 0 ? (gross / revenue) * 100 : 0, exp, net: gross - exp, byField };
  }, [rows, expenses]);

  // aliases: { billingName: hubBusinessName } — "same client, different name".
  // dismissedBilling / dismissedHub: names Phil said to stop flagging
  // (e.g. self-managed clients that are never added to the hub).
  const aliases = settings.aliases || {};
  const dismissedBilling = settings.dismissedBilling || [];
  const dismissedHub = settings.dismissedHub || [];

  const checks = useMemo(() => {
    const activeClients = clients.filter(c => !c.isArchived);
    const archivedClients = clients.filter(c => c.isArchived);
    const billingClients = [...new Set(rows.filter(r => r.service !== 'Hosting').map(r => r.client))];
    const matches = (billingName, hubName) =>
      aliases[billingName] === hubName || nameMatch(billingName, hubName);

    const unknownBilling = billingClients
      .filter(name => !dismissedBilling.includes(name))
      .filter(name => !activeClients.some(c => matches(name, c.businessName)))
      .map(name => ({
        name,
        archived: archivedClients.some(c => matches(name, c.businessName)),
      }));
    const unbilledActive = activeClients
      .map(c => c.businessName)
      .filter(hubName => !dismissedHub.includes(hubName))
      .filter(hubName => !billingClients.some(name => matches(name, hubName)));
    return { unknownBilling, unbilledActive, activeClientNames: activeClients.map(c => c.businessName).sort() };
  }, [rows, clients, aliases, dismissedBilling, dismissedHub]);

  // ----- actions -----
  const saveRow = async (id, data) => {
    if (id) await updateDoc(doc(db, 'residuals', id), { ...data, updatedAt: serverTimestamp() });
    else await addDoc(collection(db, 'residuals'), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    setEditingId(null); setAdding(false); setAddingFor(null);
  };
  const removeRow = async (r) => {
    if (window.confirm(`Delete ${r.client} (${r.service})?`)) await deleteDoc(doc(db, 'residuals', r.id));
  };

  const saveExpenses = async (items) => {
    await setDoc(doc(db, 'residualsMeta', 'expenses'), { items, updatedAt: serverTimestamp() }, { merge: true });
  };

  // updateDoc (not setDoc merge) so removed alias keys / restored names actually go away.
  const settingsRef = doc(db, 'residualsMeta', 'settings');
  const setAlias = (billingName, hubName) => updateDoc(settingsRef, { aliases: { ...aliases, [billingName]: hubName } });
  const unlinkAlias = (billingName) => {
    const next = { ...aliases }; delete next[billingName];
    return updateDoc(settingsRef, { aliases: next });
  };
  const dismissBilling = (name) => updateDoc(settingsRef, { dismissedBilling: [...dismissedBilling, name] });
  const dismissHub = (name) => updateDoc(settingsRef, { dismissedHub: [...dismissedHub, name] });
  const restoreBilling = (name) => updateDoc(settingsRef, { dismissedBilling: dismissedBilling.filter(n => n !== name) });
  const restoreHub = (name) => updateDoc(settingsRef, { dismissedHub: dismissedHub.filter(n => n !== name) });

  const saveSnapshotNow = async () => {
    const id = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Los_Angeles' }).slice(0, 7);
    await setDoc(doc(db, 'residualsSnapshots', id), {
      month: id,
      revenue: totals.revenue, hardCost: totals.cost, grossProfit: totals.gross,
      marginPct: Math.round(totals.margin * 10) / 10,
      expensesTotal: totals.exp, net: totals.net, rowCount: rows.length,
      rows: sorted.map(({ id: _id, createdAt, updatedAt, ...rest }) => rest),
      source: 'manual', createdAt: serverTimestamp(),
    });
    setSnapMsg(`Saved snapshot for ${id}.`);
    setTimeout(() => setSnapMsg(''), 4000);
  };

  // ----- gates -----
  if (gate === 'loading') return <div className="loading-screen"><div className="spinner-lg"></div></div>;
  if (gate === 'denied') return (
    <div style={{ textAlign: 'center', paddingTop: 100, color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 40 }}>⛔</div>
      <p>This page is restricted.</p>
    </div>
  );
  if (gate !== 'open') return <PinGate mode={gate} title="Residuals" onSubmit={handlePin} error={gateError} />;

  // ----- open page -----
  const summaryCards = [
    { label: 'Monthly Revenue', value: money(totals.revenue), color: 'var(--text-primary)' },
    { label: 'Hard Costs', value: money(totals.cost), color: 'var(--accent-orange)' },
    { label: 'Gross Profit', value: money(totals.gross), color: 'var(--accent-green)' },
    { label: 'Margin', value: totals.margin.toFixed(1) + '%', color: marginColor(totals.margin) },
    { label: 'Payroll + Expenses', value: money(totals.exp), color: 'var(--accent-orange)' },
    { label: 'Net (profit-ish)', value: money(totals.net), color: totals.net >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' },
  ];

  const toggleExpand = (client) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(client)) next.delete(client); else next.add(client);
    return next;
  });
  const COLS = 7 + COST_FIELDS.length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Residuals</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-secondary" onClick={saveSnapshotNow}>📸 Save snapshot</button>
          <button className="btn btn-sm btn-secondary" onClick={lock}>🔒 Lock</button>
        </div>
      </div>
      {snapMsg && <div style={{ color: 'var(--accent-green)', fontSize: 13, marginBottom: 10 }}>{snapMsg}</div>}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        {summaryCards.map(c => (
          <div key={c.label} className="card" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-secondary)', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Cross-checks against the hub client list */}
      {(checks.unknownBilling.length > 0 || checks.unbilledActive.length > 0
        || Object.keys(aliases).length > 0 || dismissedBilling.length > 0 || dismissedHub.length > 0) && (
        <div className="card" style={{ marginBottom: 20, borderColor: (checks.unknownBilling.length || checks.unbilledActive.length) ? 'var(--accent-orange)' : 'var(--border)' }}>
          <div className="card-title" style={{ cursor: 'pointer' }} onClick={() => setShowChecks(v => !v)}>
            {(checks.unknownBilling.length || checks.unbilledActive.length)
              ? `⚠️ Billing checks (${checks.unknownBilling.length + checks.unbilledActive.length})`
              : '✓ Billing checks clear'} {showChecks ? '▾' : '▸'}
          </div>
          {showChecks && (
            <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {checks.unknownBilling.map(u => (
                <div key={u.name} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>
                    <span style={{ color: 'var(--accent-orange)' }}>●</span>{' '}
                    <strong>{u.name}</strong> has a billing line but {u.archived ? 'is ARCHIVED in the hub — still billing them?' : 'isn’t in the hub’s client list.'}
                  </span>
                  {!u.archived && (
                    <select value="" onChange={e => e.target.value && setAlias(u.name, e.target.value)}
                      style={{ padding: '3px 6px', fontSize: 12, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                      <option value="">Same client, different name…</option>
                      {checks.activeClientNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  )}
                  <button className="btn btn-sm btn-secondary" onClick={() => dismissBilling(u.name)}
                    title="Stop flagging — e.g. a client you manage yourself and never add to the hub">Dismiss</button>
                </div>
              ))}
              {checks.unbilledActive.map(name => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>
                    <span style={{ color: 'var(--accent-blue)' }}>●</span>{' '}
                    Hub client <strong>{name}</strong> has no line on this page — missing revenue?
                  </span>
                  <button className="btn btn-sm btn-secondary" onClick={() => dismissHub(name)}>Dismiss</button>
                </div>
              ))}
              {(Object.keys(aliases).length > 0 || dismissedBilling.length > 0 || dismissedHub.length > 0) && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 12, borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {Object.entries(aliases).map(([b, h]) => (
                    <div key={b}>🔗 {b} = {h} <button className="btn-logout" style={{ fontSize: 11 }} onClick={() => unlinkAlias(b)}>unlink</button></div>
                  ))}
                  {dismissedBilling.map(n => (
                    <div key={n}>🙈 {n} (billing line, not in hub) <button className="btn-logout" style={{ fontSize: 11 }} onClick={() => restoreBilling(n)}>restore</button></div>
                  ))}
                  {dismissedHub.map(n => (
                    <div key={n}>🙈 {n} (hub client, no billing) <button className="btn-logout" style={{ fontSize: 11 }} onClick={() => restoreHub(n)}>restore</button></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Billing table */}
      <div className="card" style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px 0' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Client Billing ({grouped.length} clients · {rows.length} lines)</div>
          {!adding && <button className="btn btn-sm btn-primary" onClick={() => { setAdding(true); setEditingId(null); setAddingFor(null); }}>+ Add line</button>}
        </div>
        {adding && (
          <div style={{ padding: '12px 16px' }}>
            <RowEditor onSave={(d) => saveRow(null, d)} onCancel={() => setAdding(false)} />
          </div>
        )}
        <div style={{ overflow: 'auto', maxHeight: '75vh', marginTop: 10 }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                {/* border-collapse drops sticky borders, so the divider is an inset shadow */}
                {['Client', 'Services', ...COST_FIELDS.map(f => f.label), 'Hard Cost', 'Retail', 'Profit', 'Margin', ''].map((label, i) => (
                  <th key={i} style={{
                    position: 'sticky', top: 0, zIndex: 2,
                    textAlign: i >= 2 && label ? 'right' : 'left',
                    boxShadow: 'inset 0 -1px 0 var(--border)',
                  }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.map((g, gi) => {
                const isOpen = expanded.has(g.client);
                return (
                  <React.Fragment key={g.client}>
                    {/* Client summary row — click to expand its service lines */}
                    <tr onClick={() => toggleExpand(g.client)} style={{ background: ZEBRA[gi % 2], cursor: 'pointer' }}>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-block', width: 14, color: 'var(--text-secondary)' }}>{isOpen ? '▾' : '▸'}</span>
                        {g.client}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {g.list.map(r => (
                          <span key={r.id} style={{
                            fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                            background: PILL.bg, color: PILL.text, marginRight: 4,
                          }}>{r.service}</span>
                        ))}
                      </td>
                      {COST_FIELDS.map(f => (
                        <td key={f.key} style={{ textAlign: 'right', color: g[f.key] > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {g[f.key] > 0 ? money(g[f.key]) : '–'}
                        </td>
                      ))}
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(g.cost)}</td>
                      <td style={{ textAlign: 'right' }}>{money(g.retail)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: g.profit < 0 ? 'var(--accent-red)' : 'var(--text-primary)' }}>{money(g.profit)}</td>
                      <td style={{ textAlign: 'right', color: marginColor(g.margin), fontWeight: 600 }}>{g.margin === null ? '—' : g.margin.toFixed(0) + '%'}</td>
                      <td></td>
                    </tr>
                    {/* Expanded: one editable sub-row per service line */}
                    {isOpen && g.list.map(r => {
                      if (editingId === r.id) return (
                        <tr key={r.id}><td colSpan={COLS} style={{ padding: 8, background: 'var(--bg-tertiary)' }}>
                          <RowEditor initial={r} onSave={(d) => saveRow(r.id, d)} onCancel={() => setEditingId(null)} />
                        </td></tr>
                      );
                      const m = marginPct(r);
                      return (
                        <tr key={r.id} style={{ background: 'var(--bg-tertiary)' }}>
                          <td style={{ color: 'var(--text-secondary)', fontSize: 12, paddingLeft: 30 }}>↳</td>
                          <td>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                              background: PILL.bg, color: PILL.text,
                            }}>{r.service}</span>
                          </td>
                          {COST_FIELDS.map(f => (
                            <td key={f.key} style={{ textAlign: 'right', fontSize: 12, color: num(r[f.key]) > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                              {num(r[f.key]) > 0 ? money(num(r[f.key])) : '–'}
                            </td>
                          ))}
                          <td style={{ textAlign: 'right', fontSize: 12 }}>{money(hardCost(r))}</td>
                          <td style={{ textAlign: 'right', fontSize: 12 }}>{money(num(r.retail))}</td>
                          <td style={{ textAlign: 'right', fontSize: 12, color: profit(r) < 0 ? 'var(--accent-red)' : 'var(--text-primary)' }}>{money(profit(r))}</td>
                          <td style={{ textAlign: 'right', fontSize: 12, color: marginColor(m) }}>{m === null ? '—' : m.toFixed(0) + '%'}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button className="btn btn-sm btn-secondary" onClick={() => { setEditingId(r.id); setAdding(false); setAddingFor(null); }}>✏️</button>{' '}
                            <button className="btn btn-sm btn-danger" onClick={() => removeRow(r)}>🗑</button>
                          </td>
                        </tr>
                      );
                    })}
                    {isOpen && (
                      <tr style={{ background: 'var(--bg-tertiary)' }}>
                        <td colSpan={COLS} style={{ padding: addingFor === g.client ? 8 : '4px 12px 8px 30px' }}>
                          {addingFor === g.client
                            ? <RowEditor initial={{ client: g.client }} onSave={(d) => { saveRow(null, d); }} onCancel={() => setAddingFor(null)} />
                            : <button className="btn btn-sm btn-secondary" onClick={() => { setAddingFor(g.client); setEditingId(null); setAdding(false); }}>+ Add service</button>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 30 }}>
                  No billing lines yet. Add one above, or run the seed import.
                </td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  {[
                    ['TOTAL', {}],
                    ['', {}],
                    ...COST_FIELDS.map(f => [money(totals.byField[f.key]), { textAlign: 'right' }]),
                    [money(totals.cost), { textAlign: 'right' }],
                    [money(totals.revenue), { textAlign: 'right' }],
                    [money(totals.gross), { textAlign: 'right', color: 'var(--accent-green)' }],
                    [totals.margin.toFixed(1) + '%', { textAlign: 'right', color: marginColor(totals.margin) }],
                    ['', {}],
                  ].map(([value, style], i) => (
                    <td key={i} style={{
                      position: 'sticky', bottom: 0, zIndex: 2,
                      background: 'var(--bg-tertiary)',
                      boxShadow: 'inset 0 2px 0 var(--border)',
                      ...style,
                    }}>{value}</td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Expenses */}
      <ExpensesCard expenses={expenses} onSave={saveExpenses} grossProfit={totals.gross} />

      {/* Snapshots */}
      <div className="card" style={{ marginBottom: 40 }}>
        <div className="card-title">Monthly History</div>
        {snapshots.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            No snapshots yet. One is saved automatically on the 1st of each month, or use “Save snapshot” above.
          </p>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Month</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
                <th style={{ textAlign: 'right' }}>Hard Costs</th>
                <th style={{ textAlign: 'right' }}>Gross</th>
                <th style={{ textAlign: 'right' }}>Expenses</th>
                <th style={{ textAlign: 'right' }}>Net</th>
                <th style={{ textAlign: 'right' }}>Lines</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(s => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.month || s.id}</td>
                  <td style={{ textAlign: 'right' }}>{money(s.revenue || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{money(s.hardCost || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{money(s.grossProfit || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{money(s.expensesTotal || 0)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: (s.net || 0) >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>{money(s.net || 0)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{s.rowCount || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------- Expenses card ----------
function ExpensesCard({ expenses, onSave, grossProfit }) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState([]);

  const startEdit = () => { setItems((expenses.items || []).map(i => ({ ...i }))); setEditing(true); };
  const set = (idx, k, v) => setItems(list => list.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const total = (list) => list.reduce((s, i) => s + num(i.amount), 0);
  const saved = expenses.items || [];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Payroll & Expenses</div>
        {!editing && <button className="btn btn-sm btn-secondary" onClick={startEdit}>✏️ Edit</button>}
      </div>
      <div style={{ marginTop: 12 }}>
        {!editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
            {saved.length === 0 && <span style={{ color: 'var(--text-secondary)' }}>No expenses recorded.</span>}
            {saved.map((i, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                <span>{i.name}</span><span style={{ fontWeight: 600 }}>{money(num(i.amount))}</span>
              </div>
            ))}
            {saved.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360, borderTop: '1px solid var(--border)', paddingTop: 6, fontWeight: 700 }}>
                  <span>Total expenses</span><span>{money(total(saved))}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360, fontWeight: 700, color: grossProfit - total(saved) >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  <span>Net after expenses</span><span>{money(grossProfit - total(saved))}</span>
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((i, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8 }}>
                <input value={i.name} onChange={e => set(idx, 'name', e.target.value)} placeholder="Name"
                  style={{ width: 220, padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
                <input type="number" value={i.amount} onChange={e => set(idx, 'amount', e.target.value)}
                  style={{ width: 110, padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
                <button className="btn btn-sm btn-danger" onClick={() => setItems(list => list.filter((_, i2) => i2 !== idx))}>🗑</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm btn-secondary" onClick={() => setItems(list => [...list, { name: '', amount: 0 }])}>+ Add item</button>
              <button className="btn btn-sm btn-success" onClick={() => { onSave(items.filter(i => i.name.trim()).map(i => ({ name: i.name.trim(), amount: num(i.amount) }))); setEditing(false); }}>Save</button>
              <button className="btn btn-sm btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
